import React, {useState} from 'react';
import {includes} from 'lodash';
import {useDispatch} from 'react-redux';
import {Table, Checkbox, Popup, Confirm, Header, Input, Container, Grid, GridColumn} from 'semantic-ui-react';
import {ordersStatus, ordersColors} from '../../../common/dictionaries/accessories';
import {findValueInObjectsArray} from '../../../common/util/array';
import {Actions} from '../../../common/actions';
import moment from 'moment';
import {reverseShortDate} from '../../../common/constants/datesFormat';

const {
  changeChassisMarked,
  changeOrdersMarked,
  updateSatellitalDeviceOrder,
  updateDeviceOrder,
} = Actions;

const getInvoiceType = (anticipatedInvoice) => {
  return anticipatedInvoice && anticipatedInvoice === 'S' ? 'ANTICIPADA' : 'NORMAL';
};

const TableDetailBody = ({
  type,
  chassisMarked,
  ordersMarked,
  ordersFiltered,
  orderStatusList,
  filtersState,
}) => {
  const dispatch = useDispatch();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [orderToUninstall, setOrderToUninstall] = useState();
  const [reasonField, setReasonField] = useState();

  const getOrderColor = (status, worked) => {
    if (status === ordersStatus.anticipated_invoiced_order && worked === 'S') {
      return ordersColors[ordersStatus.invoiced_order];
    }
    return ordersColors[status];
  };

  const updateValuesMarked = (type, checked, value) => {
    const newArray = type === 'chassis' ? [...chassisMarked] : [...ordersMarked];
    const index = newArray.indexOf(value);

    if (checked) {
      if (index === -1) {
        newArray.push(value);
        if (type === 'chassis') {
          dispatch(changeChassisMarked(newArray));
        } else {
          dispatch(changeOrdersMarked(newArray));
        }
      }
    } else {
      if (index !== -1) {
        newArray.splice(index, 1);
        if (type === 'chassis') {
          dispatch(changeChassisMarked(newArray));
        } else {
          dispatch(changeOrdersMarked(newArray));
        }
      }
    }
  };

  const onOrderItemCheckboxChange = (checked, chassis, order) => {
    updateValuesMarked('chassis', checked, chassis);
    if (order) {
      updateValuesMarked('order', checked, order);
    }
  };

  const disableStatusToggle = (order) => {
    return (order.anticipatedInvoice === 'S' && order.worked === 'S') ||
      (order.anticipatedInvoice === 'N' && order.status === ordersStatus.invoiced_order);
  };

  const renderStatusName = (status) => {
    if (!status) {
      return '';
    }
    return findValueInObjectsArray(
      orderStatusList,
      {'keySearch': 'id', 'valueSearch': status},
      'description'
    );
  };

  const handleStatusToggleOnChange = (e, value, order) => {
    e.preventDefault();
    if (order.anticipatedInvoice === 'S') {
      dispatch(updateDeviceOrder({'order': order.order, 'install_device': value}, filtersState));
    } else {
      if (value) {
        dispatch(updateSatellitalDeviceOrder({'chassis': order.chassis, 'installType': 'E'},
          filtersState));
      } else {
        setShowConfirmationModal(true);
        setOrderToUninstall(order);
      }
    }
  };

  const onChangeReasonField = (e, value) => {
    e.preventDefault();
    setReasonField(value);
  };

  const confirmUninstall = () => {
    dispatch(updateSatellitalDeviceOrder(
      {'chassis': orderToUninstall.chassis, 'installType': 'I', 'reason': reasonField}, filtersState));
    setShowConfirmationModal(false);
  };

  if (!ordersFiltered.length) {
    return (
      <Table.Body>
        <Table.Row textAlign='center'>
          <Table.Cell colSpan='15'>
            No hay órdenes
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    );
  }

  return (
    <>
      <Table.Body>
        {ordersFiltered.map((order, index) => {
          const isNegative = !order.tasks && !order.order && type === 'accessories';
          const colorData = getOrderColor(order.status, order.worked);
          const styles = colorData ?
            {'backgroundColor': colorData.background, 'color': colorData.color} :
            {};

          return (
            <Table.Row key={index} negative={isNegative} style={styles} textAlign='center'>
              <Table.Cell>
                <Checkbox
                  id={`chassis-checkbox-${index}`}
                  disabled={type === 'accessories' && !order.tasks}
                  checked={includes(chassisMarked, order.chassis)}
                  onChange={(e, {checked}) =>
                    onOrderItemCheckboxChange(checked, order.chassis, order.order)
                  }
                />
              </Table.Cell>
              <Table.Cell>{order.dealer}</Table.Cell>
              <Table.Cell>{order.agency}</Table.Cell>
              <Table.Cell>{order.location}</Table.Cell>
              <Table.Cell>{order.parkingSlot}</Table.Cell>
              <Table.Cell>{order.description}</Table.Cell>
              <Table.Cell>{order.chassis}</Table.Cell>
              <Table.Cell>{order.color}</Table.Cell>
              <Table.Cell style={{'whiteSpace': 'nowrap'}}>
                {order.movementDate && moment(order.movementDate).format(reverseShortDate)}
              </Table.Cell>
              <Table.Cell style={{'whiteSpace': 'nowrap'}}>
                {order.hunterDate && moment(order.hunterDate).format(reverseShortDate)}
              </Table.Cell>
              <Table.Cell>
                {order.hunter ? order.hunter === 'S' ? 'HUNTER' : order.hunter === 'C' ? 'CUSTODIO' : '' : ''}
              </Table.Cell>
              <Table.Cell>{getInvoiceType(order.anticipatedInvoice)}</Table.Cell>
              <Table.Cell className='order-code'>{order.order}</Table.Cell>
              <Table.Cell textAlign='center' className='actions-column' style={{'paddingLeft': 0, 'paddingRight': 0}}>
                {order.order &&
                  <Popup content={order.hunterCloseDate ? 'Desinstalar dispositivo' : 'Instalar dispositivo'}
                    trigger={
                      <Checkbox toggle checked={order.hunterCloseDate ? true : false} className='green'
                        disabled={disableStatusToggle(order)}
                        onChange={(e, input) => handleStatusToggleOnChange(e, input.checked, order)} />
                    } />
                }
              </Table.Cell>
              <Table.Cell>{renderStatusName(order.status)}</Table.Cell>
              <Table.Cell style={{'whiteSpace': 'nowrap'}}>
                {order.hunterCloseDate && moment(order.hunterCloseDate).format(reverseShortDate)}
              </Table.Cell>
            </Table.Row>
          );
        })}
        <Confirm open={showConfirmationModal}
          header={
            <Header>¿Confirma la desinstalación del dispositivo y anulación de la orden&nbsp;
              {orderToUninstall && orderToUninstall.order}?</Header>
          }
          content={
            <Grid padded>
              <GridColumn>
                <Container>
                  <Header as='h5' style={{'backgroundColor': 'transparent'}}>Ingrese una justificación:</Header>
                  <Input fluid value={reasonField} onChange={(e, input) => onChangeReasonField(e, input.value)} />
                </Container>
              </GridColumn>
            </Grid>
          }
          onCancel={() => setShowConfirmationModal(false)}
          onConfirm={() => confirmUninstall()} />
      </Table.Body>
    </>
  );
};

export default TableDetailBody;
